import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";

const slide = {
  enter: (d) => ({ x: d > 0 ? "35%" : "-35%", opacity: 0, scale: 0.97 }),
  center: { x: 0, opacity: 1, scale: 1 },
  exit: (d) => ({ x: d > 0 ? "-35%" : "35%", opacity: 0, scale: 0.97 }),
};

function MediaItem({ item, name }) {
  if (item.type === "video") {
    return (
      <video
        data-testid="gallery-video"
        src={item.src}
        poster={item.poster}
        controls
        playsInline
        preload="metadata"
        className="h-full w-full bg-black object-contain"
      />
    );
  }
  return (
    <img
      data-testid="gallery-image"
      src={item.src}
      alt={name}
      draggable={false}
      className="h-full w-full select-none object-cover"
    />
  );
}

export default function MediaGallery({ media, name, t }) {
  const [[index, dir], setState] = useState([0, 0]);
  const count = media.length;
  const item = media[index];

  const go = useCallback(
    (delta) => setState(([i]) => [(i + delta + count) % count, delta]),
    [count],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);

  return (
    <div className="flex flex-col gap-3 md:h-full" data-testid="product-gallery">
      <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-white/10 bg-black sm:aspect-[4/3] md:aspect-auto md:min-h-0 md:flex-1">
        <AnimatePresence initial={false} custom={dir} mode="popLayout">
          <motion.div
            key={index}
            custom={dir}
            variants={slide}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            drag={item.type === "image" && count > 1 ? "x" : false}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.18}
            onDragEnd={(_, info) => {
              if (info.offset.x < -60) go(1);
              else if (info.offset.x > 60) go(-1);
            }}
            className="absolute inset-0"
          >
            <MediaItem item={item} name={name} />
          </motion.div>
        </AnimatePresence>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/60 to-transparent" />

        <span
          data-testid="gallery-counter"
          className="absolute right-4 top-4 rounded-full border border-white/15 bg-black/55 px-3 py-1 font-mono text-[10px] tracking-widest text-white backdrop-blur-md"
        >
          {index + 1} / {count}
        </span>

        {count > 1 && (
          <>
            <button
              data-testid="gallery-prev"
              onClick={() => go(-1)}
              aria-label="Previous"
              className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/55 text-white backdrop-blur-md transition-colors hover:border-ember hover:text-ember"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              data-testid="gallery-next"
              onClick={() => go(1)}
              aria-label="Next"
              className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/55 text-white backdrop-blur-md transition-colors hover:border-ember hover:text-ember"
            >
              <ChevronRight size={16} />
            </button>
            <span className="absolute bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.3em] text-white/60 md:hidden">
              {t.product.swipe}
            </span>
          </>
        )}
      </div>

      {count > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="gallery-thumbs">
          {media.map((m, i) => (
            <button
              key={`${m.src}-${i}`}
              data-testid={`gallery-thumb-${i}`}
              onClick={() => setState(([cur]) => [i, i > cur ? 1 : -1])}
              className={`relative h-16 w-16 shrink-0 overflow-hidden rounded-xl border transition-all duration-300 sm:h-20 sm:w-20 ${
                i === index ? "border-ember shadow-[0_0_18px_rgba(255,107,0,0.35)]" : "border-white/10 opacity-60 hover:opacity-100"
              }`}
            >
              {m.type === "video" ? (
                <>
                  {m.poster ? (
                    <img src={m.poster} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <video src={m.src} muted preload="metadata" className="h-full w-full object-cover" />
                  )}
                  <span className="absolute inset-0 flex items-center justify-center bg-black/40 text-white">
                    <Play size={14} fill="currentColor" />
                  </span>
                </>
              ) : (
                <img src={m.src} alt="" className="h-full w-full object-cover" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
